import type { FC } from 'react';
import { Suspense, useEffect } from 'react';
import type { SelectProps } from './Select';
import CommonSelect from './CommonSelect';
import { useGroups } from '../../api/getGroups';
import { Skeleton } from '@mui/material';
import { UserRole } from 'features/login';
import { useFormContext } from 'react-hook-form';
import datadogActions from 'lib/datadog';

interface SelectListProps {
  options?: 'all' | 'short';
  multiples?: boolean;
}

type SelectGroupsProps = Partial<
  Pick<
    SelectProps,
    'handleChange' | 'name' | 'inputLabel' | 'placeholder' | 'required' | 'disabled'
  >
> &
  Partial<SelectListProps>;

const SelectGroupsComponent: FC<SelectGroupsProps> =
  ({
     handleChange,
     name,
     inputLabel,
     placeholder,
     multiples,
     required,
     disabled,
   }) => {
    const { setValue, watch } = useFormContext();
    const { data, isError, error } = useGroups({
      config: {
        select:
          (data) =>
            data.map((group) => ({ label: group.name, value: group.id })),
      },
    });

    const isRoleSuperuser = watch('role') === UserRole.SUPERUSER;

    useEffect(() => {
      if (isRoleSuperuser) {
        setValue(name ?? 'groups', data?.map(d => d.value) ?? [],
          { shouldValidate: true, shouldDirty: true });
      }
    }, [data, isRoleSuperuser, setValue, name]);

    useEffect(() => {
      if (!isError || !error) {
        return;
      }

      datadogActions.trackDatadogError(error as Error, {
        source: 'common_select_groups_query',
      });
    }, [isError, error]);

    return (
      <CommonSelect
        name={name ?? 'groups'}
        inputLabel={inputLabel ?? 'Groups'}
        options={data ?? []}
        handleChange={handleChange}
        placeholder={placeholder ?? 'Value'}
        clearable
        multiples={multiples}
        required={required}
        disabled={isRoleSuperuser || disabled}
      />
    );
  };


const SelectGroups: FC<SelectGroupsProps> =
  (props) => {
    return (
      <Suspense
        fallback={
          <Skeleton
            height={40}
            sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
          />
        }>
        <SelectGroupsComponent {...props} />
      </Suspense>
    );
  };

export default SelectGroups;
